// src/pages/CallLogPage.js
// 通話紀錄頁面 — AI 電話客服通話紀錄管理（V7 design）

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Drawer,
  Snackbar,
  Alert,
  Pagination,
  Skeleton,
  Stack,
  Tooltip,
  Button,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ListIcon from '@mui/icons-material/List';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import EditIcon from '@mui/icons-material/Edit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import TimerIcon from '@mui/icons-material/Timer';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Layout from '../components/Layout';
import ImageViewer from '../components/ImageViewer';
import SingleDateNavigator from '../components/dashboard/SingleDateNavigator';
import callLogService from '../services/callLogService';
import { io } from 'socket.io-client';
import authService from '../services/authService';
import dayjs from 'dayjs';
import { formatTime, formatDuration } from '../utils/callLogFormat';
import CallTranscriptView from '../components/conversation/CallTranscriptView';

// 狀態配色 — 24-value taxonomy（migration 094 + openspec/changes/add-call-log-status-taxonomy）
// 家族配色：
//   success  (#4caf50 green)  — 成功完成商業動作
//   callback (#00897b teal)   — 已安排後續回電
//   neutral  (#9e9e9e gray)   — 客戶得到正確資訊但沒訂位
//   constraint(#ff9800 amber) — 限制條件觸發（AI 正確說明）
//   transfer (#145da0 blue)   — 轉接真人
//   abandoned(#d32f2f red)    — 客戶中斷
//   error    (#7e57c2 purple) — 系統錯誤
//   legacy   (#9e9e9e gray)   — pre-migration rows
const STATUS_CONFIG = {
  // success
  booking_completed:    { label: '已訂位',     color: '#4caf50' },
  booking_cancelled:    { label: '已取消訂位', color: '#4caf50' },
  booking_modified:     { label: '已修改訂位', color: '#4caf50' },
  reminder_confirmed:   { label: '已確認出席', color: '#4caf50' },
  reminder_cancelled:   { label: '取消訂位',   color: '#4caf50' },
  reminder_modified:    { label: '修改訂位',   color: '#4caf50' },
  // callback
  callback_scheduled:       { label: '已安排回電',     color: '#00897b' },
  reminder_needs_callback:  { label: '提醒待專人回電', color: '#00897b' },
  // neutral
  faq_answered:         { label: 'FAQ 已答覆',   color: '#9e9e9e' },
  no_booking_found:     { label: '查無訂位',     color: '#9e9e9e' },
  reminder_no_response: { label: '提醒未接聽',   color: '#9e9e9e' },
  // constraint
  blocked_date:      { label: '檔期封鎖', color: '#ff9800' },
  no_availability:   { label: '全日客滿', color: '#ff9800' },
  // transfer
  transferred_private_room:  { label: '轉接真人 (包廂)', color: '#145da0' },
  transferred_group_booking: { label: '轉接真人 (團訂)', color: '#145da0' },
  transferred_max_retries:   { label: '轉接真人 (重試)', color: '#145da0' },
  transferred_other:         { label: '轉接真人',       color: '#145da0' },
  // abandoned / error
  caller_abandoned: { label: '客戶中斷', color: '#d32f2f' },
  system_error:     { label: '系統錯誤', color: '#7e57c2' },
  unclassified:     { label: '未分類',   color: '#9e9e9e' },
  // legacy (pre-migration rows)
  completed:   { label: '完成 (舊)', color: '#9e9e9e' },
  transferred: { label: '轉接 (舊)', color: '#9e9e9e' },
  hungup:      { label: '掛斷 (舊)', color: '#9e9e9e' },
  error:       { label: '錯誤 (舊)', color: '#9e9e9e' },
};

// 狀態 badge 配色（drawer 用）— same taxonomy with softer background tints
const STATUS_BADGE = {
  // success
  booking_completed:    { label: '已訂位',     bg: '#e8f5e9', color: '#2e7d32' },
  booking_cancelled:    { label: '已取消訂位', bg: '#e8f5e9', color: '#2e7d32' },
  booking_modified:     { label: '已修改訂位', bg: '#e8f5e9', color: '#2e7d32' },
  reminder_confirmed:   { label: '已確認出席', bg: '#e8f5e9', color: '#2e7d32' },
  reminder_cancelled:   { label: '取消訂位',   bg: '#e8f5e9', color: '#2e7d32' },
  reminder_modified:    { label: '修改訂位',   bg: '#e8f5e9', color: '#2e7d32' },
  // callback
  callback_scheduled:       { label: '已安排回電',     bg: '#e0f2f1', color: '#00695c' },
  reminder_needs_callback:  { label: '提醒待專人回電', bg: '#e0f2f1', color: '#00695c' },
  // neutral
  faq_answered:         { label: 'FAQ 已答覆',   bg: '#f5f5f5', color: '#616161' },
  no_booking_found:     { label: '查無訂位',     bg: '#f5f5f5', color: '#616161' },
  reminder_no_response: { label: '提醒未接聽',   bg: '#f5f5f5', color: '#616161' },
  // constraint
  blocked_date:      { label: '檔期封鎖', bg: '#fff3e0', color: '#e65100' },
  no_availability:   { label: '全日客滿', bg: '#fff3e0', color: '#e65100' },
  // transfer
  transferred_private_room:  { label: '轉接真人 (包廂)', bg: '#e3f2fd', color: '#0d47a1' },
  transferred_group_booking: { label: '轉接真人 (團訂)', bg: '#e3f2fd', color: '#0d47a1' },
  transferred_max_retries:   { label: '轉接真人 (重試)', bg: '#e3f2fd', color: '#0d47a1' },
  transferred_other:         { label: '轉接真人',       bg: '#e3f2fd', color: '#0d47a1' },
  // abandoned / error
  caller_abandoned: { label: '客戶中斷', bg: '#fdecea', color: '#c62828' },
  system_error:     { label: '系統錯誤', bg: '#ede7f6', color: '#4527a0' },
  unclassified:     { label: '未分類',   bg: '#f5f5f5', color: '#616161' },
  // legacy
  completed:   { label: '完成 (舊分類)', bg: '#f5f5f5', color: '#616161' },
  transferred: { label: '轉接 (舊分類)', bg: '#f5f5f5', color: '#616161' },
  hungup:      { label: '掛斷 (舊分類)', bg: '#f5f5f5', color: '#616161' },
  error:       { label: '錯誤 (舊分類)', bg: '#f5f5f5', color: '#616161' },
};

// 依意圖決定 completed 狀態的 badge 標籤（預設值在 DEFAULT_COMPLETED_BADGE_LABELS）

// 團體訂位 badge（transferred 狀態，人數 > 12）
const GROUP_BOOKING_BADGE = { label: '團體訂位', bg: '#fce4ec', color: '#c62828' };

// 判斷是否為團體訂位
const isGroupBooking = (log) => {
  const slots = log.slots || {};
  const totalParty = Number(slots.total_party || slots.adults || 0);
  return totalParty > 12;
};

// Tab 設定
const TAB_CONFIG = [
  { value: 'transferred', label: '轉接真人', icon: <PhoneCallbackIcon sx={{ fontSize: 16 }} />, countKey: 'transferred' },
  { value: 'completed', label: '完成', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />, countKey: 'completed' },
  { value: 'cancelled', label: '取消', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />, countKey: 'cancelled' },
  { value: 'modified', label: '修改', icon: <EditIcon sx={{ fontSize: 16 }} />, countKey: 'modified' },
  { value: 'hungup', label: '客戶掛斷', icon: <PhoneMissedIcon sx={{ fontSize: 16 }} />, countKey: 'hungup' },
  { value: 'error', label: '異常', icon: <WarningAmberIcon sx={{ fontSize: 16 }} />, countKey: 'error' },
  { value: 'all', label: '全部', icon: <ListIcon sx={{ fontSize: 16 }} />, countKey: 'total' },
];

// ===== 預設值（未設定 callLogConfig 的公司使用）=====

const DEFAULT_HANDLING_TAG_OPTIONS = [
  '已改包廂', '已改時間', '已改人數', '需再確認', '客戶取消',
  '團體桌菜', '已回電', '未接聽', '稍後再撥', '已完成',
];

const DEFAULT_INTENT_LABELS = {
  booking: '訂位',
  check: '查詢',
  cancel: '取消',
  modify: '修改',
  group: '團體桌菜',
  private_room: '包廂',
  inquiry: '詢問',
  complaint: '客訴',
  other: '其他',
};

const DEFAULT_COMPLETED_BADGE_LABELS = {
  booking: '訂位完成',
  check: '查詢完成',
  cancel: '取消完成',
  modify: '修改完成',
  group: '團體桌菜',
};

const DEFAULT_END_REASON_LABELS = {
  callback_transfer: '需回撥',
  transfer_human: '轉接真人',
  ai_unable_to_handle: 'AI 無法處理',
};

const DEFAULT_SLOT_VALUE_LABELS = {
  want_callback: { yes: '是', no: '否' },
  needs_more: { true: '是', false: '否' },
  confirmed: { true: '是', false: '否' },
  cancel_confirmed: { true: '是', false: '否' },
  modify_what: { datetime: '日期時間', party_size: '人數', both: '日期時間與人數' },
  new_intent: { booking: '訂位', check: '查詢', cancel: '取消', modify: '修改', repeat: '重複', question: '提問', other: '其他' },
  accept_alt: { yes: '是', no: '否' },
  try_other_times: { yes: '是', no: '否', change_date: '換日期' },
};

// 格式化時間 — MM/DD HH:mm
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
};

// ────────────────────────────────────────
// Sticky header cell style (FaqPage pattern)
// ────────────────────────────────────────
const stickyHeaderSx = {
  bgcolor: '#ddd',
  fontWeight: 'bold',
  fontSize: '14px',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  borderBottom: '1px solid #ccc',
};

const CallLogPage = () => {
  // 資料狀態
  const [callLogs, setCallLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });

  // 篩選狀態
  const [activeTab, setActiveTab] = useState('all');
  const [direction, setDirection] = useState('all'); // all | inbound | outbound
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState(dayjs().format('YYYY-MM-DD'));

  // Drawer 狀態
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerView, setDrawerView] = useState('detail'); // 'detail' | 'conversation'
  const [screenshotViewerOpen, setScreenshotViewerOpen] = useState(false);

  // WebSocket 狀態
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);

  // 回報處理
  const [editMemo, setEditMemo] = useState('');
  const [editMemoTags, setEditMemoTags] = useState([]);
  const [saving, setSaving] = useState(false);

  // 字體大小（平板用）
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('calllog_fontsize');
    return saved ? Number(saved) : 14;
  });
  const handleFontSizeChange = (size) => {
    setFontSize(size);
    localStorage.setItem('calllog_fontsize', size);
  };

  // 通知
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

  // 公司自訂 UI 設定（從 API 取得）
  const [uiConfig, setUiConfig] = useState(null);

  // 動態標籤（依 uiConfig 或使用預設值）
  const INTENT_LABELS = uiConfig?.intentLabels || DEFAULT_INTENT_LABELS;
  const HANDLING_TAG_OPTIONS = uiConfig?.handlingTags || DEFAULT_HANDLING_TAG_OPTIONS;
  const COMPLETED_BADGE_LABELS = uiConfig?.completedBadgeLabels || DEFAULT_COMPLETED_BADGE_LABELS;
  const END_REASON_LABELS = uiConfig?.endReasonLabels || DEFAULT_END_REASON_LABELS;
  const SLOT_VALUE_LABELS = uiConfig?.slotValueLabels || DEFAULT_SLOT_VALUE_LABELS;
  const SLOT_LABELS = { ...DEFAULT_SLOT_LABELS, ...(uiConfig?.slotLabels || {}) };

  // 載入 UI 設定
  useEffect(() => {
    const loadUiConfig = async () => {
      const result = await callLogService.getUiConfig();
      if (result.success && result.data) {
        setUiConfig(result.data);
      }
    };
    loadUiConfig();
  }, []);

  // 載入通話紀錄
  const fetchCallLogs = useCallback(async () => {
    setLoading(true);
    const result = await callLogService.getCallLogs({
      page: pagination.page,
      limit: pagination.limit,
      status: activeTab,
      date: dateFilter,
      search: searchQuery,
      direction,
    });
    if (result.success) {
      setCallLogs(result.data || []);
      setPagination(prev => ({ ...prev, ...result.pagination }));
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, activeTab, dateFilter, searchQuery, direction]);

  // 載入統計
  const fetchStats = useCallback(async () => {
    const result = await callLogService.getStats(dateFilter || undefined);
    if (result.success) setStats(result.data);
  }, [dateFilter]);

  useEffect(() => {
    fetchCallLogs();
    fetchStats();
  }, [fetchCallLogs, fetchStats]);

  // WebSocket：per-page io() 連線 + 監聽 call_log 即時更新
  // （mirror RestaurantReservationsPage 慣例；避免共用 socketService wrapper 的註冊 race）
  useEffect(() => {
    const companyId = authService.getCompanyId();
    if (!companyId) return undefined;

    const socketUrl = process.env.REACT_APP_SOCKET_URL
      || process.env.REACT_APP_API_URL?.replace('/api', '')
      || window.location.origin;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');

    const handleCallLogCreated = () => {
      fetchCallLogs();
      fetchStats();
    };
    const handleCallLogUpdated = (update) => {
      // 即時更新截圖 URL（背景截圖完成時觸發）
      if (update?.id && update?.screenshotUrl) {
        setCallLogs(prev => prev.map(l =>
          l.id === update.id ? { ...l, screenshotUrl: update.screenshotUrl } : l
        ));
        setSelectedLog(prev =>
          prev?.id === update.id ? { ...prev, screenshotUrl: update.screenshotUrl } : prev
        );
      }
    };

    try {
      const socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      const joinRoom = () => {
        setSocketConnected(true);
        socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      };
      socket.on('connect', joinRoom);
      socket.on('reconnect', joinRoom);
      socket.on('disconnect', () => setSocketConnected(false));
      socket.on('call_log.created', handleCallLogCreated);
      socket.on('call_log.updated', handleCallLogUpdated);
      socketRef.current = socket;
    } catch (e) { /* socket 失敗不擋頁面 */ }

    return () => {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    };
  }, [fetchCallLogs, fetchStats]);

  // 搜尋防抖
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500));
  };

  // Tab 切換
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // 打開 Drawer
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setEditMemo(log.memo || '');
    setEditMemoTags(log.memoTags || []);
    setDrawerView('detail');
    setDrawerOpen(true);
  };

  // 完成處理
  const handleComplete = async () => {
    if (!selectedLog) return;
    setSaving(true);
    const result = await callLogService.updateCallLog(selectedLog.id, {
      handled: true,
      memo: editMemo,
      memoTags: editMemoTags,
    });
    if (result.success) {
      setCallLogs(prev => prev.map(l =>
        l.id === selectedLog.id ? { ...l, handled: true, memo: editMemo, memoTags: editMemoTags } : l
      ));
      setSelectedLog(prev => ({ ...prev, handled: true, memo: editMemo, memoTags: editMemoTags }));
      setNotification({ open: true, message: '已完成處理', type: 'success' });
      fetchStats();
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setSaving(false);
  };

  // 切換回報標籤
  const toggleMemoTag = (tag) => {
    setEditMemoTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  return (
    <Layout>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>

        {/* ── Header ── */}
        <Box sx={{ flexShrink: 0, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, pt: 2.5, pb: 0 }}>
          {/* Title row + stats */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700 }}>通話紀錄</Typography>
                <Tooltip title={socketConnected ? 'WebSocket 即時更新已連線' : 'WebSocket 未連線'}>
                  <Box component="span" sx={{
                    width: 10, height: 10,
                    bgcolor: socketConnected ? '#4caf50' : '#bdbdbd',
                    borderRadius: '50%',
                    display: 'inline-block',
                  }} />
                </Tooltip>
              </Box>
              <Typography sx={{ fontSize: 13, color: '#999' }}>查看所有 AI 電話客服的通話紀錄與處理狀態</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }}>
              <StatItem label="今日來電" value={stats?.total || 0} color="#333" />
              <StatItem label="訂位完成" value={stats?.completed || 0} color="#333" />
              <StatItem label="待回撥" value={stats?.pendingCallback || 0} color="#e65100" />
              <StatItem label="平均時長" value={stats?.avgDurationSeconds ? formatDuration(stats.avgDurationSeconds) : '—'} color="#333" />
            </Box>
          </Box>

          {/* 來電/去電 切換 */}
          <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
            {[
              { value: 'all', label: '全部' },
              { value: 'inbound', label: '來電' },
              { value: 'outbound', label: '去電' },
            ].map(opt => (
              <Box
                key={opt.value}
                onClick={() => {
                  setDirection(opt.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                sx={{
                  px: 1.5, py: 0.5, fontSize: 13, cursor: 'pointer', borderRadius: 1,
                  bgcolor: direction === opt.value ? '#145da0' : '#f0f0f0',
                  color: direction === opt.value ? '#fff' : '#444',
                  fontWeight: direction === opt.value ? 600 : 400,
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Box>

          {/* Tabs with icons + count badges */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': { backgroundColor: '#145da0' },
              '& .MuiTab-root': {
                minHeight: 40, py: 0, textTransform: 'none', fontSize: '14px',
                fontWeight: 500, color: '#666',
                '&.Mui-selected': { color: '#145da0', fontWeight: 600 },
              },
            }}
          >
            {TAB_CONFIG.map(tab => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {tab.icon}
                    <span>{tab.label}</span>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12, fontWeight: 600, px: 1, py: '1px', borderRadius: '10px',
                        bgcolor: activeTab === tab.value ? '#e3f2fd'
                          : tab.value === 'transferred' ? '#fff3e0' : '#eee',
                        color: tab.value === 'transferred' && activeTab !== tab.value ? '#e65100' : 'inherit',
                      }}
                    >
                      {stats?.[tab.countKey] ?? 0}
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* ── Search row ── */}
        <Box sx={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5,
          px: 3, py: 1.5, bgcolor: '#f9f9f9', borderBottom: '1px solid #f0f0f0',
        }}>
          <TextField
            size="small"
            placeholder="搜尋電話、姓名..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
          />
          <SingleDateNavigator
            value={dateFilter}
            onChange={(d) => {
              setDateFilter(d);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
          {/* 字體大小切換 */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: '#999', mr: 0.5 }}>字體</Typography>
            {[14, 16, 18, 20].map(size => (
              <Box
                key={size}
                onClick={() => handleFontSizeChange(size)}
                sx={{
                  px: 1, py: '3px', borderRadius: '6px', cursor: 'pointer',
                  fontSize: 12, fontWeight: fontSize === size ? 700 : 400,
                  bgcolor: fontSize === size ? '#145da0' : '#eee',
                  color: fontSize === size ? 'white' : '#666',
                  '&:hover': { bgcolor: fontSize === size ? '#145da0' : '#ddd' },
                }}
              >
                {size}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Table ── */}
        <Box sx={{
          flex: 1, overflow: 'auto', px: 3,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
        }}>
          <TableContainer component={Paper} sx={{ mt: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="4%" align="center" sx={stickyHeaderSx}>#</TableCell>
                  <TableCell width="12%" sx={stickyHeaderSx}>時間</TableCell>
                  <TableCell width="12%" sx={stickyHeaderSx}>來電者</TableCell>
                  <TableCell width="12%" sx={stickyHeaderSx}>電話號碼</TableCell>
                  <TableCell width="8%" sx={stickyHeaderSx}>意圖</TableCell>
                  <TableCell width="8%" align="center" sx={stickyHeaderSx}>時長</TableCell>
                  <TableCell width="10%" align="center" sx={stickyHeaderSx}>狀態</TableCell>
                  <TableCell width="18%" sx={stickyHeaderSx}>備註</TableCell>
                  <TableCell width="10%" align="center" sx={stickyHeaderSx}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : callLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#999' }}>
                      <PhoneIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography>暫無通話紀錄</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  callLogs.map((log, idx) => {
                    let statusCfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.completed;
                    if (log.status === 'transferred' && isGroupBooking(log)) {
                      statusCfg = { label: '團體訂位', color: '#c62828' };
                    }
                    const rowNum = (pagination.page - 1) * pagination.limit + idx + 1;
                    const isHandledTransfer = log.status === 'transferred' && log.handled;
                    const dimmedSx = isHandledTransfer ? { opacity: 0.45, textDecoration: 'line-through' } : {};
                    return (
                      <TableRow
                        key={log.id}
                        hover
                        onClick={() => handleRowClick(log)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isHandledTransfer ? '#f9f9f9' : (idx % 2 === 0 ? 'white' : '#fffef5'),
                          '&:hover': { bgcolor: '#f0f4ff !important' },
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <TableCell align="center" sx={{ py: 1.75, fontSize, ...dimmedSx }}>{rowNum}</TableCell>
                        <TableCell sx={{ py: 1.75, fontSize, ...dimmedSx }}>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell sx={{ py: 1.75, fontSize, fontWeight: log.callerName ? 500 : 400, color: log.callerName ? '#333' : '#ccc', ...dimmedSx }}>
                          {log.callerName || '—'}
                        </TableCell>
                        <TableCell sx={{ py: 1.75, fontSize, ...dimmedSx }}>{log.callerNumber || '—'}</TableCell>
                        <TableCell sx={{ py: 1.75 }}>
                          {log.intent ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Box component="span" sx={{
                                display: 'inline-block', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0',
                                color: '#555', fontSize: 12, px: 1, py: '2px', borderRadius: '12px',
                              }}>
                                {INTENT_LABELS[log.intent] || log.intent}
                              </Box>
                              {log.slots?.new_intent && log.slots.new_intent !== log.intent && (
                                <Box component="span" sx={{
                                  display: 'inline-block', bgcolor: '#e3f2fd', border: '1px solid #90caf9',
                                  color: '#1565c0', fontSize: 12, px: 1, py: '2px', borderRadius: '12px',
                                }}>
                                  {INTENT_LABELS[log.slots.new_intent] || log.slots.new_intent}
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="#ccc">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.75, ...dimmedSx }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: '#666' }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: '#999' }} />
                            <Typography variant="body2">{formatDuration(log.durationSeconds)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.75, ...dimmedSx }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, fontWeight: 500, color: '#333' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusCfg.color, flexShrink: 0 }} />
                            {isHandledTransfer ? `${statusCfg.label} ✓` : statusCfg.label}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.75 }}>
                          {log.memoTags?.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {log.memoTags.map(tag => (
                                <Box key={tag} component="span" sx={{
                                  fontSize: 11, px: 1, py: '2px', borderRadius: '10px',
                                  bgcolor: '#f0f4ff', color: '#145da0', border: '1px solid #d0dfff',
                                }}>
                                  {tag}
                                </Box>
                              ))}
                            </Box>
                          ) : log.memo ? (
                            <Typography variant="body2" noWrap sx={{ maxWidth: 160, color: '#666' }}>{log.memo}</Typography>
                          ) : (
                            <Typography variant="body2" color="#ccc">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.75 }} onClick={e => e.stopPropagation()}>
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="通話詳情">
                              <IconButton
                                size="small"
                                onClick={() => handleRowClick(log)}
                                sx={{
                                  width: 30, height: 30, borderRadius: '6px',
                                  bgcolor: 'rgba(3,169,244,0.08)',
                                  '&:hover': { bgcolor: 'rgba(3,169,244,0.18)' },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16, color: '#03a9f4' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="對話紀錄">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedLog(log);
                                  setDrawerView('conversation');
                                  setDrawerOpen(true);
                                }}
                                sx={{
                                  width: 30, height: 30, borderRadius: '6px',
                                  bgcolor: 'rgba(25,118,210,0.08)',
                                  '&:hover': { bgcolor: 'rgba(25,118,210,0.18)' },
                                }}
                              >
                                <ChatIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ── Pagination ── */}
        <Box sx={{
          flexShrink: 0, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0',
          p: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2,
        }}>
          <Pagination
            count={pagination.totalPages || 1}
            page={pagination.page}
            onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
            size="small"
            shape="rounded"
          />
          <Select
            size="small"
            value={pagination.limit}
            onChange={(e) => setPagination(prev => ({ ...prev, limit: e.target.value, page: 1 }))}
            sx={{ height: 32, fontSize: 13 }}
          >
            {[10, 20, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {/* ═══ Drawer ═══ */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 440, overflow: 'hidden' } }}
      >
        {selectedLog && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {drawerView === 'detail' ? (
              <DetailView
                log={selectedLog}
                fontSize={fontSize}
                editMemo={editMemo}
                setEditMemo={setEditMemo}
                editMemoTags={editMemoTags}
                toggleMemoTag={toggleMemoTag}
                handleComplete={handleComplete}
                saving={saving}
                onViewConversation={() => setDrawerView('conversation')}
                onViewScreenshot={() => setScreenshotViewerOpen(true)}
                onClose={() => setDrawerOpen(false)}
                COMPLETED_BADGE_LABELS={COMPLETED_BADGE_LABELS}
                INTENT_LABELS={INTENT_LABELS}
                END_REASON_LABELS={END_REASON_LABELS}
                HANDLING_TAG_OPTIONS={HANDLING_TAG_OPTIONS}
                SLOT_LABELS={SLOT_LABELS}
                SLOT_VALUE_LABELS={SLOT_VALUE_LABELS}
              />
            ) : (
              <ConversationView
                log={selectedLog}
                fontSize={fontSize}
                onBack={() => setDrawerView('detail')}
                onClose={() => setDrawerOpen(false)}
              />
            )}
          </Box>
        )}
      </Drawer>

      {/* 訂位截圖 ImageViewer */}
      {selectedLog?.screenshotUrl && (
        <ImageViewer
          open={screenshotViewerOpen}
          onClose={() => setScreenshotViewerOpen(false)}
          images={[{
            url: selectedLog.screenshotUrl,
            name: `訂位截圖 — ${selectedLog.callerName || ''} ${selectedLog.slots?.date || ''}`,
          }]}
          initialIndex={0}
          title="訂位確認截圖"
        />
      )}

      {/* 通知 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

// ── Stat item (header) ──
const StatItem = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: '#999', mt: '2px' }}>{label}</Typography>
  </Box>
);

// ── Detail View ──
const DetailView = ({ log, fontSize: fs = 14, editMemo, setEditMemo, editMemoTags, toggleMemoTag, handleComplete, saving, onViewConversation, onViewScreenshot, onClose, COMPLETED_BADGE_LABELS = DEFAULT_COMPLETED_BADGE_LABELS, INTENT_LABELS = DEFAULT_INTENT_LABELS, END_REASON_LABELS = DEFAULT_END_REASON_LABELS, HANDLING_TAG_OPTIONS = DEFAULT_HANDLING_TAG_OPTIONS, SLOT_LABELS = DEFAULT_SLOT_LABELS, SLOT_VALUE_LABELS = DEFAULT_SLOT_VALUE_LABELS }) => {
  const baseBadge = STATUS_BADGE[log.status] || STATUS_BADGE.completed;
  const slots = log.slots || {};
  // 依狀態和意圖顯示不同標籤
  let badge;
  if (log.status === 'transferred' && isGroupBooking(log)) {
    badge = GROUP_BOOKING_BADGE;
  } else if (log.status === 'completed' && log.intent) {
    // 以最終動作為主（查詢→修改 → 顯示「修改完成」）
    const finalIntent = log.slots?.new_intent || log.intent;
    let completedLabel = COMPLETED_BADGE_LABELS[finalIntent] || baseBadge.label;

    // 訂位意圖但未完成（無電話或未確認）→ 顯示「未完成訂位」
    if (finalIntent === 'booking' && !slots.phone && !slots.confirmed) {
      completedLabel = '未完成訂位';
      badge = { label: completedLabel, bg: '#fff8e1', color: '#f57c00' };
    } else if (finalIntent === 'cancel' && !slots.cancel_confirmed) {
      completedLabel = '未完成取消';
      badge = { label: completedLabel, bg: '#fff8e1', color: '#f57c00' };
    } else if (finalIntent === 'modify' && !log.slots?.modify_confirmed) {
      // 修改：檢查 api_result 是否成功（slots 可能沒有 modify_confirmed）
      const modifySuccess = log.api_results?.modify_execute?.success;
      if (modifySuccess === false) {
        completedLabel = '未完成修改';
        badge = { label: completedLabel, bg: '#fff8e1', color: '#f57c00' };
      } else {
        badge = { ...baseBadge, label: completedLabel };
      }
    } else {
      badge = { ...baseBadge, label: completedLabel };
    }
  } else {
    badge = baseBadge;
  }

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>通話詳情</Typography>
        <IconButton size="small" onClick={onClose} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
          <CloseIcon sx={{ fontSize: 18, color: '#666' }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Status bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1.5, py: 0.5, borderRadius: '14px', fontSize: 13, fontWeight: 600,
            bgcolor: badge.bg, color: badge.color,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: badge.color }} />
            {badge.label}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {log.handled && (
              <Box sx={{ fontSize: 12, fontWeight: 600, color: '#4caf50', bgcolor: '#e8f5e9', px: 1, py: '2px', borderRadius: '10px' }}>
                已處理 ✓
              </Box>
            )}
            <Typography sx={{ fontSize: 13, color: '#999' }}>
              {formatTime(log.created_at)} &middot; {formatDuration(log.durationSeconds)}
            </Typography>
          </Box>
        </Box>

        {/* Info section */}
        <Typography sx={{ fontSize: 12, color: '#999', fontWeight: 500, px: 2.5, pt: 1.5, pb: 0.5 }}>收集的資料</Typography>

        <InfoRow icon={<PersonIcon />} label="來電者" value={log.callerName || '—'} fs={fs} />
        <InfoRow icon={<PhoneInTalkIcon />} label="電話" value={log.callerNumber || '—'} fs={fs} />
        <InfoRow icon={<CheckCircleOutlineIcon />} label="意圖" value={INTENT_LABELS[log.intent] || log.intent || '—'} fs={fs} />
        {log.status === 'transferred' && log.endReason && (
          <InfoRow icon={<NotificationsActiveIcon />} label="轉接原因" value={END_REASON_LABELS[log.endReason] || log.endReason} fs={fs} />
        )}
        <InfoRow icon={<TimerIcon />} label="通話時長" value={log.durationSeconds ? `${log.durationSeconds} 秒` : '—'} fs={fs} />
        <BookingSlots slots={slots} fs={fs} slotLabels={SLOT_LABELS} slotValueLabels={SLOT_VALUE_LABELS} />

        {/* Action buttons */}
        <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            onClick={onViewConversation}
            disabled={!log.transcript || log.transcript.length === 0}
            sx={{
              textTransform: 'none', borderRadius: 2, fontSize: 14, fontWeight: 500,
              bgcolor: '#145da0', '&:hover': { bgcolor: '#0f4a82' },
              px: 2.5, py: 1,
            }}
          >
            查看對話
          </Button>
          {log.screenshotUrl && (
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={onViewScreenshot}
              sx={{
                textTransform: 'none', borderRadius: 2, fontSize: 14, fontWeight: 500,
                borderColor: '#4caf50', color: '#4caf50',
                '&:hover': { borderColor: '#388e3c', bgcolor: '#f1f8e9' },
                px: 2.5, py: 1,
              }}
            >
              訂位截圖
            </Button>
          )}
        </Box>

        {/* Handling section */}
        <Box sx={{ borderTop: '1px solid #e0e0e0', px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#999', fontWeight: 500, mb: 1.25 }}>回報處理</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.75 }}>
            {HANDLING_TAG_OPTIONS.map(tag => (
              <Box
                key={tag}
                onClick={() => toggleMemoTag(tag)}
                sx={{
                  px: 1.5, py: '5px', borderRadius: '16px', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', transition: '.15s',
                  border: '1.5px solid',
                  ...(editMemoTags.includes(tag) ? {
                    bgcolor: '#e65100', color: 'white', borderColor: '#e65100',
                  } : {
                    bgcolor: 'white', color: '#c07020', borderColor: '#e0a050',
                    '&:hover': { bgcolor: '#fff8f0' },
                  }),
                }}
              >
                {tag}
              </Box>
            ))}
          </Box>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="備註..."
            value={editMemo}
            onChange={(e) => setEditMemo(e.target.value)}
            size="small"
            sx={{
              mb: 1.75,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 14 },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleComplete}
            disabled={saving}
            sx={{
              textTransform: 'none', borderRadius: 2, py: 1.5, fontSize: 15, fontWeight: 600,
              bgcolor: '#145da0', '&:hover': { bgcolor: '#0f4a82' },
            }}
          >
            {saving ? '處理中...' : '完成處理'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

// ── Info row (detail drawer) ──
const InfoRow = ({ icon, label, value, fs = 14 }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
    <Box sx={{ mt: '2px', color: '#999', '& svg': { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: fs - 2, color: '#999', mb: '1px' }}>{label}</Typography>
      <Typography sx={{ fontSize: fs + 1, fontWeight: 500, color: '#333' }}>{value}</Typography>
    </Box>
  </Box>
);

// ── Booking slots (detail drawer) — only show booking-specific fields ──
// Fields already shown separately (來電者, 電話, 意圖) or internal fields to exclude
const EXCLUDED_SLOT_KEYS = new Set([
  'name', 'gender', 'phone', 'intent',
  'confirmed', 'needs_more', 'new_intent',
  'no_avail_choice', 'cancel_confirmed', 'modify_confirmed',
  'cancel_retry', 'modify_retry',
  'callback_more', 'business_hours_retry',
]);

// search_* slots are rendered in a special grouped view, so exclude from generic list
const SEARCH_SLOT_PATTERN = /^search_/;

const SLOT_ORDER = [
  'date', 'time', 'total_party', 'adults', 'children', 'kid_chairs', 'duck',
];

const DEFAULT_SLOT_LABELS = {
  date: '日期',
  time: '時間',
  total_party: '總人數',
  adults: '大人',
  children: '小孩',
  kid_chairs: '兒童座椅',
  duck: '烤鴨',
  caller_id: '來電號碼',
  want_callback: '須回撥',
  phone: '電話',
  name: '姓名',
  gender: '性別',
  confirmed: '已確認',
  needs_more: '需要其他服務',
  new_intent: '新意圖',
  cancel_phone: '電話',
  cancel_name: '姓名',
  cancel_date: '日期',
  cancel_time: '時間',
  cancel_adults: '大人',
  cancel_children: '小孩',
  cancel_table: '桌號',
  cancel_confirmed: '確認取消',
  modify_phone: '電話',
  modify_name: '姓名',
  modify_orig_date: '原訂日期',
  modify_orig_time: '原訂時間',
  modify_orig_adults: '原訂人數',
  modify_orig_children: '原訂小孩',
  modify_orig_table: '原訂桌號',
  modify_orig_duck: '原訂烤鴨',
  modify_orig_kid_chairs: '原訂兒童椅',
  modify_what: '修改項目',
  new_date: '新日期',
  new_time: '新時間',
  new_adults: '新人數',
  new_children: '新小孩數',
  alt_time: '替代時間',
  accept_alt: '接受替代',
  try_other_times: '查看其他時段',
  availability_retry: '可用性重試',
  no_avail_choice: '無位選擇',
  retry_time: '重試時間',
  prev_date: '前次日期',
  search_count: '查詢次數',
};

// 從 slots 中提取查詢結果，組成結構化的訂位陣列
const extractSearchBookings = (slots) => {
  const name = slots.search_name;
  if (!name) return null;

  const bookings = [];

  // 單筆: search_date, search_time, search_party
  if (slots.search_date && !slots.search_date_1) {
    bookings.push({ date: slots.search_date, time: slots.search_time, party: slots.search_party });
  }

  // 多筆: search_date_1, search_date_2, search_date_3 ...
  for (let i = 1; i <= 10; i++) {
    const date = slots[`search_date_${i}`];
    if (!date) break;
    bookings.push({
      date,
      time: slots[`search_time_${i}`],
      party: slots[`search_party_${i}`],
    });
  }

  return bookings.length > 0 ? { name, bookings } : null;
};

const BookingSlots = ({ slots, fs = 14, slotLabels = DEFAULT_SLOT_LABELS, slotValueLabels = DEFAULT_SLOT_VALUE_LABELS }) => {
  if (!slots) return null;

  // 檢查是否有查詢結果 (search_* slots)
  const searchData = extractSearchBookings(slots);

  // 非 search 的一般 slots
  const filtered = Object.entries(slots)
    .filter(([k, v]) =>
      !EXCLUDED_SLOT_KEYS.has(k) &&
      !SEARCH_SLOT_PATTERN.test(k) &&
      v != null && v !== '' && v !== false && v !== 0
    );

  // Sort: ordered keys first, then remaining keys
  const keyMap = new Map(filtered);
  const entries = [
    ...SLOT_ORDER.filter(k => keyMap.has(k)).map(k => [k, keyMap.get(k)]),
    ...filtered.filter(([k]) => !SLOT_ORDER.includes(k)),
  ];

  const hasGenericSlots = entries.length > 0;
  const hasSearchData = !!searchData;

  if (!hasGenericSlots && !hasSearchData) return null;

  return (
    <>
      {/* 查詢結果 — 結構化顯示 */}
      {hasSearchData && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
          <Box sx={{ mt: '2px', color: '#999', '& svg': { fontSize: 20 } }}><DescriptionIcon /></Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#999', mb: '4px' }}>查詢結果</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: '2px' }}>
              <Typography sx={{ fontSize: fs, color: '#666' }}>姓名</Typography>
              <Typography sx={{ fontSize: fs, fontWeight: 500, color: '#333' }}>{searchData.name}</Typography>
            </Box>
            {searchData.bookings.map((b, idx) => (
              <Box key={idx} sx={{
                mt: 1, p: 1.25, borderRadius: 1.5,
                bgcolor: '#f8f9fa', border: '1px solid #e8eaed',
              }}>
                {searchData.bookings.length > 1 && (
                  <Typography sx={{ fontSize: fs - 2, fontWeight: 600, color: '#145da0', mb: 0.5 }}>
                    訂位 {idx + 1}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: '1px' }}>
                  <Typography sx={{ fontSize: fs, color: '#666' }}>日期</Typography>
                  <Typography sx={{ fontSize: fs, fontWeight: 500, color: '#333' }}>{b.date}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: '1px' }}>
                  <Typography sx={{ fontSize: fs, color: '#666' }}>時間</Typography>
                  <Typography sx={{ fontSize: fs, fontWeight: 500, color: '#333' }}>{b.time || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: '1px' }}>
                  <Typography sx={{ fontSize: fs, color: '#666' }}>人數</Typography>
                  <Typography sx={{ fontSize: fs, fontWeight: 500, color: '#333' }}>{b.party ? `${b.party} 位` : '—'}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 一般 slots */}
      {hasGenericSlots && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
          <Box sx={{ mt: '2px', color: '#999', '& svg': { fontSize: 20 } }}><DescriptionIcon /></Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#999', mb: '4px' }}>
              {hasSearchData ? '其他資料' : '已蒐集資料'}
            </Typography>
            {entries.map(([k, v]) => {
              const displayVal = slotValueLabels[k]?.[v] ?? String(v);
              return (
                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: '2px' }}>
                  <Typography sx={{ fontSize: fs, color: '#666' }}>{slotLabels[k] || k}</Typography>
                  <Typography sx={{ fontSize: fs, fontWeight: 500, color: '#333' }}>{displayVal}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

// ── Conversation View (omnichat style) ──
// ConversationView 已抽出為共用元件 CallTranscriptView（與訂位後台「通話紀錄」抽屜共用，DRY）。
// 保留同名別名，呼叫端 <ConversationView .../> 不需改動。
const ConversationView = CallTranscriptView;

export default CallLogPage;
